import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import CustomDatePicker from './CustomDatePicker';

const SectionE = ({ formData: parentFormData, onDataChange, onValidationChange }) => {
    // Initialize nominees from parentFormData
    const initialNominees = parentFormData?.nomineeE && Array.isArray(parentFormData.nomineeE)
        ? parentFormData.nomineeE
        : [];

    const [showModal, setShowModal] = useState(false);
    const [nominees, setNominees] = useState(initialNominees);

    // Refs to prevent circular updates
    const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
    const isUpdatingFromParentRef = useRef(false);
    const lastSentToParentRef = useRef(JSON.stringify({ nomineeE: nominees }));
    const nomineesRef = useRef(nominees);

    // Update ref when nominees change
    useEffect(() => {
        nomineesRef.current = nominees;
    }, [nominees]);
    const [editIndex, setEditIndex] = useState(null);
    const [formData, setFormData] = useState({
        FirstName: '',
        RelationshipWthNomineeName: '',
        NomineePercentage: '',
        DateOfBirth: '',
        G_FirstName: '',
        G_LastName: '',
        G_MiddleName: ''
    });
    const [isMinor, setIsMinor] = useState(false);
    const [percentageError, setPercentageError] = useState('');
    const [errors, setErrors] = useState({
        FirstName: '',
        RelationshipWthNomineeName: '',
        NomineePercentage: '',
        GuardianName: ''
    });

    const relationshipOptions = [
        "Father",
        "Mother",
        "Son",
        "Wife",
        "Husband",
        "Brother",
        "Sister",
        "Daughter",
        "Other",
    ];

    // Calculate total percentage
    const calculateTotalPercentage = () => {
        return nominees.reduce((total, nominee) => {
            return total + parseFloat(nominee.NomineePercentage || 0);
        }, 0);
    };

    // Calculate remaining percentage
    const getRemainingPercentage = () => {
        const total = calculateTotalPercentage();
        return Math.max(0, 100 - total);
    };

    // Check if nominee is minor (under 18 years)
    // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
    const convertDDMMYYYYToYYYYMMDD = (ddmmyyyy) => {
        if (!ddmmyyyy) return '';
        const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
        if (ddmmyyyyPattern.test(ddmmyyyy)) {
            const parts = ddmmyyyy.split('-');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return ddmmyyyy;
    };

    // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
    const convertYYYYMMDDToDDMMYYYY = (yyyymmdd) => {
        if (!yyyymmdd) return '';
        const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
        if (yyyymmddPattern.test(yyyymmdd)) {
            const parts = yyyymmdd.split('-');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        // If already in DD-MM-YYYY format, return as is
        const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
        if (ddmmyyyyPattern.test(yyyymmdd)) {
            return yyyymmdd;
        }
        return yyyymmdd;
    };

    const checkIfMinor = (dateOfBirth) => {
        if (!dateOfBirth) return false;

        const dateForCheck = convertDDMMYYYYToYYYYMMDD(dateOfBirth) || dateOfBirth;

        const today = new Date();
        const birthDate = new Date(dateForCheck);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age < 18;
    };

    // Update parent when nominees change
    useEffect(() => {
        if (isUpdatingFromParentRef.current) {
            return;
        }

        const currentData = JSON.stringify({ nomineeE: nominees });
        if (currentData !== lastSentToParentRef.current) {
            lastSentToParentRef.current = currentData;

            if (onDataChange) {
                onDataChange({ nomineeE: nominees });
            }
        }
    }, [nominees, onDataChange]);

    // Update from parent data (API response)
    useEffect(() => {
        if (!parentFormData) {
            return;
        }

        // Check if nomineeE exists (even if empty array)
        if (!parentFormData.hasOwnProperty('nomineeE')) {
            return;
        }

        const currentParentData = JSON.stringify(parentFormData);
        if (currentParentData === lastParentDataRef.current) {
            return;
        }

        // Handle both empty and non-empty arrays
        if (Array.isArray(parentFormData.nomineeE)) {
            const currentNominees = JSON.stringify(nomineesRef.current);
            const newNominees = JSON.stringify(parentFormData.nomineeE);

            if (currentNominees !== newNominees) {
                isUpdatingFromParentRef.current = true;
                setNominees(parentFormData.nomineeE);
                lastParentDataRef.current = currentParentData;

                setTimeout(() => {
                    isUpdatingFromParentRef.current = false;
                }, 100);
            }
        } else {
        }
    }, [parentFormData]);

    // Update validation status for parent component
    // Validation disabled - always report as valid
    useEffect(() => {
        // const totalPercentage = calculateTotalPercentage();
        // const isComplete = totalPercentage === 100 && nominees.length > 0;

        if (onValidationChange) {
            onValidationChange(true);
            // onValidationChange(isComplete);
        }
    }, [nominees, onValidationChange]);

    const handleOpenModal = () => {
        const remaining = getRemainingPercentage();
        setShowModal(true);
        setEditIndex(null);
        setFormData({
            FirstName: '',
            RelationshipWthNomineeName: '',
            NomineePercentage: remaining > 0 ? remaining.toString() : '',
            DateOfBirth: '',
            G_FirstName: '',
            G_LastName: '',
            G_MiddleName: ''
        });
        setIsMinor(false);
        setPercentageError('');
        setErrors({
            FirstName: '',
            RelationshipWthNomineeName: '',
            NomineePercentage: '',
            GuardianName: ''
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditIndex(null);
        setFormData({
            FirstName: '',
            RelationshipWthNomineeName: '',
            NomineePercentage: '',
            DateOfBirth: '',
            G_FirstName: '',
            G_LastName: '',
            G_MiddleName: ''
        });
        setIsMinor(false);
        setPercentageError('');
        setErrors({
            FirstName: '',
            RelationshipWthNomineeName: '',
            NomineePercentage: '',
            GuardianName: ''
        });
    };

    // Function to split guardian name by spaces
    const splitGuardianName = (guardianName) => {
        if (!guardianName || !guardianName.trim()) {
            return {
                G_FirstName: '',
                G_LastName: '',
                G_MiddleName: ''
            };
        }

        const parts = guardianName.trim().split(/\s+/);
        const partCount = parts.length;

        if (partCount === 1) {
            return {
                G_FirstName: parts[0],
                G_LastName: '',
                G_MiddleName: ''
            };
        } else if (partCount === 2) {
            return {
                G_FirstName: parts[0],
                G_LastName: parts[1],
                G_MiddleName: ''
            };
        } else if (partCount === 3) {
            return {
                G_FirstName: parts[0],
                G_MiddleName: parts[1],
                G_LastName: parts[2]
            };
        } else {
            // More than 3 words: First = first word, Middle = second word, Last = rest joined
            return {
                G_FirstName: parts[0],
                G_MiddleName: parts[1],
                G_LastName: parts.slice(2).join(' ')
            };
        }
    };

    // Validation functions - DISABLED (always return empty / valid)
    const validateFirstName = (value) => {
        return '';
        // if (!value || !value.trim()) {
        //     return 'First Name is required';
        // }
        // if (value.trim().length < 2) {
        //     return 'First Name must be at least 2 characters';
        // }
        // if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        //     return 'First Name should contain only letters';
        // }
        // return '';
    };

    const validateRelationship = (value) => {
        return '';
        // if (!value || !value.trim()) {
        //     return 'Relationship is required';
        // }
        // return '';
    };

    const validateNomineePercentage = (value, currentEditIndex = null) => {
        return '';
        // if (!value || value === '') {
        //     return 'Percentage Share is required';
        // }
        // const percentage = parseFloat(value);
        // if (isNaN(percentage)) {
        //     return 'Please enter a valid number';
        // }
        // if (percentage < 0) {
        //     return 'Percentage cannot be negative';
        // }
        // if (percentage > 100) {
        //     return 'Percentage cannot exceed 100%';
        // }

        // // Calculate what the new total would be
        // // Use the parameter if provided, otherwise use state
        // const editingIndex = currentEditIndex !== null ? currentEditIndex : editIndex;
        // let newTotal = 0;

        // if (editingIndex !== null && editingIndex >= 0) {
        //     // Exclude the current nominee's percentage from total when editing
        //     newTotal = nominees.reduce((total, nominee, index) => {
        //         if (index !== editingIndex) {
        //             return total + parseFloat(nominee.NomineePercentage || 0);
        //         }
        //         return total;
        //     }, 0) + percentage;
        // } else {
        //     // Adding new nominee - add to existing total
        //     newTotal = calculateTotalPercentage() + percentage;
        // }

        // if (newTotal > 100) {
        //     return `Total percentage cannot exceed 100%. Current total would be ${newTotal.toFixed(2)}%`;
        // }

        // return '';
    };

    const validateGuardianName = (value) => {
        return '';
        // if (!isMinor) return '';
        // if (!value || !value.trim()) {
        //     return 'Guardian name is required for minor nominees';
        // }
        // const splitName = splitGuardianName(value);
        // if (!splitName.G_FirstName || !splitName.G_LastName) {
        //     return 'Guardian First Name and Last Name are required';
        // }
        // return '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'DateOfBirth') {
            const dateForCheck = convertDDMMYYYYToYYYYMMDD(value) || value;
            const minor = checkIfMinor(dateForCheck);
            setIsMinor(minor);
            if (!minor) {
                // Clear guardian name if not minor
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    G_FirstName: '',
                    G_LastName: '',
                    G_MiddleName: ''
                }));
                setErrors(prev => ({
                    ...prev,
                    GuardianName: ''
                }));
                return;
            }
        }

        // Handle guardian name splitting and validation
        if (name === 'guardianName') {
            const splitName = splitGuardianName(value);
            setFormData(prev => ({
                ...prev,
                ...splitName
            }));
            const guardianError = validateGuardianName(value);
            setErrors(prev => ({
                ...prev,
                GuardianName: guardianError
            }));
            return;
        }

        // Validate FirstName
        if (name === 'FirstName') {
            const error = validateFirstName(value);
            setErrors(prev => ({
                ...prev,
                FirstName: error
            }));
        }

        // Validate Relationship
        if (name === 'RelationshipWthNomineeName') {
            const error = validateRelationship(value);
            setErrors(prev => ({
                ...prev,
                RelationshipWthNomineeName: error
            }));
        }

        // Validate NomineePercentage
        if (name === 'NomineePercentage') {
            const error = validateNomineePercentage(value, editIndex);
            setErrors(prev => ({
                ...prev,
                NomineePercentage: error
            }));
            setPercentageError(error);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate all fields
        const firstNameError = validateFirstName(formData.FirstName);
        const relationshipError = validateRelationship(formData.RelationshipWthNomineeName);
        const percentageError = validateNomineePercentage(formData.NomineePercentage, editIndex);
        const guardianNameValue = `${formData.G_FirstName}${formData.G_MiddleName ? ' ' + formData.G_MiddleName : ''}${formData.G_LastName ? ' ' + formData.G_LastName : ''}`.trim();
        const guardianError = validateGuardianName(guardianNameValue);

        // Set all errors
        setErrors({
            FirstName: firstNameError,
            RelationshipWthNomineeName: relationshipError,
            NomineePercentage: percentageError,
            GuardianName: guardianError
        });

        setPercentageError(percentageError);

        // Validation disabled - skip error check
        // if (firstNameError || relationshipError || percentageError || guardianError) {
        //     return;
        // }

        // Create nominee object with correct parameter names
        const nomineeData = {
            FirstName: formData.FirstName.trim(),
            RelationshipWthNomineeName: formData.RelationshipWthNomineeName,
            NomineePercentage: formData.NomineePercentage,
            DateOfBirth: formData.DateOfBirth,
            G_FirstName: formData.G_FirstName,
            G_LastName: formData.G_LastName,
            G_MiddleName: formData.G_MiddleName
        };

        if (editIndex !== null) {
            // Edit existing nominee - preserve existing fields like NomineeID, ExitFormID, etc.
            const updatedNominees = [...nominees];
            updatedNominees[editIndex] = {
                ...nominees[editIndex], // Preserve existing fields
                ...nomineeData // Update with new data
            };
            setNominees(updatedNominees);
        } else {
            // Add new nominee
            setNominees([...nominees, nomineeData]);
        }

        handleCloseModal();
    };

    const handleEdit = (index) => {
        setEditIndex(index);
        const nominee = nominees[index];
        setFormData(nominee);
        const dateForCheck = convertDDMMYYYYToYYYYMMDD(nominee.DateOfBirth) || nominee.DateOfBirth;
        setIsMinor(checkIfMinor(dateForCheck));
        setPercentageError('');

        // Validate existing data - pass the index explicitly
        const firstNameError = validateFirstName(nominee.FirstName);
        const relationshipError = validateRelationship(nominee.RelationshipWthNomineeName);
        const percentageError = validateNomineePercentage(nominee.NomineePercentage, index);
        const guardianNameValue = `${nominee.G_FirstName}${nominee.G_MiddleName ? ' ' + nominee.G_MiddleName : ''}${nominee.G_LastName ? ' ' + nominee.G_LastName : ''}`.trim();
        const guardianError = validateGuardianName(guardianNameValue);

        setErrors({
            FirstName: firstNameError,
            RelationshipWthNomineeName: relationshipError,
            NomineePercentage: percentageError,
            GuardianName: guardianError
        });

        setShowModal(true);
    };

    const handleDelete = (index) => {
        if (window.confirm('Are you sure you want to delete this nominee?')) {
            const updatedNominees = nominees.filter((_, i) => i !== index);
            setNominees(updatedNominees);
        }
    };

    return (
        <div className="section-container">
            <div className="card shadow-sm">
                <div className="card-body p-4">
                    {/* Note */}
                    <div className="alert alert-info" style={{ fontSize: '0.85rem', padding: '0.75rem' }}>
                        <strong>Note:</strong> If the nominee is minor then some details will have to be filled seperately in the exit form like, Date of birth of the nominee, Guardian name, Guardian Date of birth, Guardian address and Guardian Signature.
                    </div>

                    {/* Add Nominee Button */}
                    <div className="mb-4">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleOpenModal}
                            // Validation disabled - allow adding nominees freely
                            // disabled={calculateTotalPercentage() >= 100}
                            style={{
                                borderRadius: '25px',
                                padding: '0.5rem 1.5rem',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                border: '2px solid #0d6efd',
                                // opacity: calculateTotalPercentage() >= 100 ? 0.5 : 1,
                                // cursor: calculateTotalPercentage() >= 100 ? 'not-allowed' : 'pointer'
                                opacity: 1,
                                cursor: 'pointer'
                            }}
                        >
                            <FaPlus className="me-2" style={{ fontSize: '0.85rem' }} />
                            Add Nominee
                        </button>
                    </div>

                    {/* Nominees Table */}
                    {nominees.length > 0 ? (
                        <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead style={{ backgroundColor: '#0d6efd', color: 'white' }}>
                                    <tr>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>S.No</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Nominee Name</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Relationship</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none', textAlign: 'center' }}>Share %</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Date of Birth</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Guardian Name</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none', textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody style={{ backgroundColor: '#fff' }}>
                                    {nominees.map((nominee, index) => {
                                        const dateForCheck = convertDDMMYYYYToYYYYMMDD(nominee.DateOfBirth) || nominee.DateOfBirth;
                                        const nomineeIsMinor = checkIfMinor(dateForCheck);
                                        const guardianName = nominee.G_FirstName
                                            ? `${nominee.G_FirstName}${nominee.G_MiddleName ? ' ' + nominee.G_MiddleName : ''}${nominee.G_LastName ? ' ' + nominee.G_LastName : ''}`.trim()
                                            : '';
                                        return (
                                            <tr key={index} style={{
                                                borderBottom: index !== nominees.length - 1 ? '1px solid #e9ecef' : 'none',
                                                transition: 'background-color 0.2s'
                                            }}>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    fontWeight: '500',
                                                    color: '#666',
                                                    textAlign: 'center'
                                                }}>
                                                    {index + 1}
                                                </td>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    fontWeight: '500',
                                                    color: '#333'
                                                }}>
                                                    {nominee.FirstName || '-'}
                                                </td>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666'
                                                }}>
                                                    {nominee.RelationshipWthNomineeName || '-'}
                                                </td>
                                                <td style={{
                                                    fontSize: '0.95rem',
                                                    padding: '0.9rem 0.75rem',
                                                    textAlign: 'center',
                                                    fontWeight: '600',
                                                    color: '#0d6efd'
                                                }}>
                                                    {nominee.NomineePercentage || '0'}%
                                                </td>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666'
                                                }}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span>{nominee.DateOfBirth || '-'}</span>
                                                        {nomineeIsMinor && (
                                                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                                                                Minor
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    color: guardianName ? '#333' : '#999',
                                                    fontStyle: guardianName ? 'normal' : 'italic'
                                                }}>
                                                    {guardianName || 'N/A'}
                                                </td>
                                                <td style={{
                                                    fontSize: '0.875rem',
                                                    padding: '0.9rem 0.75rem',
                                                    textAlign: 'center'
                                                }}>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleEdit(index)}
                                                            title="Edit Nominee"
                                                            style={{
                                                                padding: '0.35rem 0.6rem',
                                                                backgroundColor: '#e7f1ff',
                                                                border: '1px solid #0d6efd',
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#0d6efd';
                                                                e.currentTarget.querySelector('svg').style.color = 'white';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#e7f1ff';
                                                                e.currentTarget.querySelector('svg').style.color = '#0d6efd';
                                                            }}
                                                        >
                                                            <FaEdit style={{ fontSize: '0.9rem', color: '#0d6efd' }} />
                                                        </button>
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleDelete(index)}
                                                            title="Delete Nominee"
                                                            style={{
                                                                padding: '0.35rem 0.6rem',
                                                                backgroundColor: '#ffe7e7',
                                                                border: '1px solid #dc3545',
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#dc3545';
                                                                e.currentTarget.querySelector('svg').style.color = 'white';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#ffe7e7';
                                                                e.currentTarget.querySelector('svg').style.color = '#dc3545';
                                                            }}
                                                        >
                                                            <FaTrash style={{ fontSize: '0.9rem', color: '#dc3545' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #0d6efd' }}>
                                    <tr>
                                        <td colSpan="3" style={{
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            padding: '1rem 0.75rem',
                                            textAlign: 'right',
                                            color: '#333'
                                        }}>
                                            <strong>Total Percentage:</strong>
                                        </td>
                                        <td style={{
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            padding: '1rem 0.75rem',
                                            textAlign: 'center',
                                            color: calculateTotalPercentage() === 100 ? '#28a745' : '#dc3545'
                                        }}>
                                            {calculateTotalPercentage().toFixed(2)}%
                                        </td>
                                        <td colSpan="3" style={{
                                            fontSize: '0.875rem',
                                            padding: '1rem 0.75rem',
                                            color: calculateTotalPercentage() === 100 ? '#28a745' : '#dc3545',
                                            fontWeight: '500'
                                        }}>
                                            {calculateTotalPercentage() === 100 ? (
                                                <span><i className="fas fa-check-circle me-1"></i>Complete</span>
                                            ) : (
                                                <span><i className="fas fa-exclamation-circle me-1"></i>Remaining: {getRemainingPercentage().toFixed(2)}%</span>
                                            )}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-5 px-4" style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                        }}>
                            <i className="fas fa-users" style={{ fontSize: '3rem', color: '#dee2e6', marginBottom: '1rem' }}></i>
                            <p style={{ color: '#6c757d', fontSize: '0.95rem', marginBottom: '0.5rem', fontWeight: '500' }}>
                                No nominees added yet
                            </p>
                            <p style={{ color: '#adb5bd', fontSize: '0.85rem', marginBottom: 0 }}>
                                Click "Add Nominee" button to add a nominee
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Nominee Modal */}
            {showModal && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 10050, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                        onClick={handleCloseModal}
                    ></div>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        style={{ zIndex: 10055 }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ opacity: 1 }}>
                                <div className="modal-header" style={{ backgroundColor: '#0d6efd', color: 'white' }}>
                                    <h5 className="modal-title" style={{ fontSize: '1rem', fontWeight: '600' }}>
                                        {editIndex !== null ? 'Edit Nominee' : 'Add Nominee'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={handleCloseModal}
                                    ></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1.5rem' }}>
                                    <form onSubmit={handleSubmit}>
                                        {/* Name */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.FirstName ? 'is-invalid' : ''}`}
                                                name="FirstName"
                                                value={formData.FirstName}
                                                onChange={handleInputChange}
                                                placeholder="Enter nominee name"
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                            {errors.FirstName && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.FirstName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Relationship */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Relationship with Nominee <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className={`form-select ${errors.RelationshipWthNomineeName ? 'is-invalid' : ''}`}
                                                name="RelationshipWthNomineeName"
                                                value={formData.RelationshipWthNomineeName}
                                                onChange={handleInputChange}
                                                style={{ fontSize: '0.875rem' }}
                                            >
                                                <option value="">Select Relationship</option>
                                                {relationshipOptions.map((option, idx) => {
                                                    // Get all selected relationships from existing nominees
                                                    const selectedRelationships = nominees
                                                        .map((nominee, index) => {
                                                            // Exclude current nominee being edited
                                                            if (editIndex !== null && index === editIndex) {
                                                                return null;
                                                            }
                                                            return nominee.RelationshipWthNomineeName;
                                                        })
                                                        .filter(Boolean);

                                                    const isDisabled = selectedRelationships.includes(option);

                                                    return (
                                                        <option
                                                            key={idx}
                                                            value={option}
                                                            disabled={isDisabled}
                                                            style={isDisabled ? { color: '#999', fontStyle: 'italic' } : {}}
                                                        >
                                                            {option}{isDisabled ? ' (Already Selected)' : ''}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {errors.RelationshipWthNomineeName && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.RelationshipWthNomineeName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Percentage Share */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Percentage Share <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                className={`form-control ${errors.NomineePercentage ? 'is-invalid' : ''}`}
                                                name="NomineePercentage"
                                                value={formData.NomineePercentage}
                                                onChange={handleInputChange}
                                                onWheel={(e) => e.target.blur()}
                                                placeholder="Enter percentage"
                                                min="0"
                                                max="100"
                                                step="1"
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                            {errors.NomineePercentage && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.NomineePercentage}
                                                </div>
                                            )}
                                        </div>

                                        {/* Date of Birth */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Date of Birth (In case of Minor)
                                            </label>
                                            <CustomDatePicker
                                                name="DateOfBirth"
                                                value={convertYYYYMMDDToDDMMYYYY(formData.DateOfBirth)}
                                                onChange={handleInputChange}
                                                maxDate={new Date().toISOString().split('T')[0]}
                                                placeholder="DD-MM-YYYY"
                                            />
                                            {isMinor && formData.DateOfBirth && (
                                                <small className="text-warning mt-1 d-block">
                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                    This nominee is a minor. Guardian information is required.
                                                </small>
                                            )}
                                        </div>

                                        {/* Guardian Name - Only show if minor */}
                                        {isMinor && (
                                            <div className="mb-3">
                                                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                    Guardian's Name <span className="text-danger">*</span>
                                                    <small className="text-muted ms-2">(Will be split into First, Middle, Last based on spaces)</small>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.GuardianName ? 'is-invalid' : ''}`}
                                                    name="guardianName"
                                                    value={`${formData.G_FirstName}${formData.G_MiddleName ? ' ' + formData.G_MiddleName : ''}${formData.G_LastName ? ' ' + formData.G_LastName : ''}`.trim()}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter guardian's full name (e.g., 'John', 'John Doe', 'John Michael Doe')"
                                                    style={{ fontSize: '0.875rem' }}
                                                />
                                                {errors.GuardianName && (
                                                    <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                        {errors.GuardianName}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Modal Footer */}
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={handleCloseModal}
                                                style={{ fontSize: '0.875rem', padding: '0.5rem 1.25rem' }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                style={{ fontSize: '0.875rem', padding: '0.5rem 1.25rem' }}
                                                // Validation disabled
                                                // disabled={!!errors.FirstName || !!errors.RelationshipWthNomineeName || !!errors.NomineePercentage || !!errors.GuardianName}
                                            >
                                                {editIndex !== null ? 'Update' : 'Submit'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SectionE;

